<template>
    <div class="trends-table slice-content" data-testid="trends-table">
        <div>
            <TitleBrand :field="title" />
            <h3 v-if="subtitle">{{ subtitle }}</h3>
        </div>
        <table class="trends-table__table">
            <thead>
                <tr>
                    <th class="trends-table__cell trends-table__cell--rank-head" />
                    <template v-for="(item, col) in columnItems" :key="`h--${col}`">
                        <th v-if="col >= 2" class="trends-table__cell trends-table__gap" aria-hidden="true" />
                        <th :class="headerColumnClass(col)">
                            <PrismicRichText :field="item.columnHead" />
                        </th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, rowIndex) in rows" :key="`r--${rowIndex}`">
                    <th class="trends-table__cell trends-table__cell--rank">
                        {{ rowIndex + 1 }}
                    </th>
                    <template v-for="(cell, col) in row" :key="`c--${rowIndex}--${col}`">
                        <td v-if="col >= 2" class="trends-table__cell trends-table__gap" aria-hidden="true" />
                        <td :class="bodyValueClass(col, rowIndex)">
                            <PrismicRichText :field="cell" />
                        </td>
                    </template>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup lang="ts">
    import type {RichTextField, KeyTextField} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import TitleBrand from '~/components/TitleBrand.vue';

    type TrendsTableColumnItem = {columnHead: RichTextField};

    const props = defineProps<{
        columnItems: TrendsTableColumnItem[];
        rows: RichTextField[][];
        subtitle: KeyTextField;
        title: RichTextField;
    }>();

    function columns(col: number) {
        const columns = ['skill', 'demand', 'supply'];
        return columns[col];
    }

    function headerColumnClass(col: number) {
        return ['trends-table__cell', `trends-table__cell--${columns(col)}`, col >= 1 && 'trends-table__cell--pill-top'];
    }

    function bodyValueClass(col: number, rowIndex: number) {
        const isLast = rowIndex === props.rows.length - 1;
        return ['trends-table__cell', `trends-table__cell--${columns(col)}`, isLast && 'trends-table__cell--pill-bottom'];
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .trends-table {
        --idx-bg: var(--joy-color-neutral-30);
        --idx-fg: var(--joy-color-neutral-0);
        --demand-bg: var(--joy-color-tertiary-10);
        --demand-fg: var(--joy-color-tertiary-70);
        --supply-bg: var(--joy-color-brand-primary-10);
        --supply-fg: var(--joy-color-brand-primary-70);

        @include mq.screen_md(min) {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-9);
        }

        h3 {
            font-size: var(--joy-font-size-primary-800);
            margin-bottom: var(--joy-core-spacing-6);
        }

        &__table {
            width: 100%;
            margin: 0 auto;

            @include mq.screen_sm(min) {
                max-width: 600px;
            }

            tbody tr:not(:last-child) > * {
                border-bottom: 1px solid var(--idx-fg);
            }

            thead th {
                font-weight: var(--joy-font-weight-bold);
                vertical-align: bottom;
                padding-bottom: var(--joy-core-spacing-3);
                border-bottom: 1px solid var(--idx-bg);
            }
        }

        &__cell {
            display: table-cell;
            vertical-align: middle;
            padding: var(--joy-core-spacing-2);
            font-size: var(--joy-font-size-primary-400);
            line-height: var(--joy-line-height-small);
            min-height: 40px;

            &--rank-head {
                display: none;
                background: transparent;
                color: inherit;

                @include mq.screen_xs(min) {
                    display: table-cell;
                    width: 10px;
                }
            }

            &--rank {
                display: none;

                @include mq.screen_xs(min) {
                    display: table-cell;
                    text-align: center;
                    background: var(--idx-bg);
                    color: var(--idx-fg);
                    font-size: var(--joy-font-size-primary-600);
                    font-weight: var(--joy-font-weight-bold);
                }
            }

            &--skill {
                background: transparent;

                @include mq.screen_xs(min) {
                    padding-inline: var(--joy-core-spacing-6);
                }
            }

            &--demand,
            &--supply {
                text-align: center;
                width: 115px;

                p {
                    color: inherit;
                }
            }

            &--demand {
                background: var(--demand-bg);
                color: var(--demand-fg);
            }

            &--supply {
                background: var(--supply-bg);
                color: var(--supply-fg);
            }

            &--pill-top {
                border-radius: var(--joy-core-radius-5) var(--joy-core-radius-5) 0 0;
                padding-top: var(--joy-core-spacing-4);
            }

            &--pill-bottom {
                border-radius: 0 0 var(--joy-core-radius-5) var(--joy-core-radius-5);
                padding-bottom: var(--joy-core-spacing-4);
            }
        }

        &__gap {
            display: none;

            @include mq.screen_xs(min) {
                display: table-cell;
                width: var(--joy-core-spacing-4);
            }
        }
    }
</style>
